import type { HmsIconName } from "./hmsIcons";

export interface ModuleFeature {
  title: string;
  icon: HmsIconName;
  description?: string;
  items?: string[];
  subsections?: { title: string; items: string[] }[];
}

export interface HmsModule {
  id: string;
  title: string;
  icon: HmsIconName;
  description: string;
  features: ModuleFeature[];
}

export interface HmsAdvantage {
  text: string;
  icon: HmsIconName;
}

export interface HmsWorkflowStep {
  title: string;
  text: string;
  icon: HmsIconName;
}

export interface HmsUserRole {
  title: string;
  icon: HmsIconName;
  intro: string;
  items: string[];
}

export const hmsModules: HmsModule[] = [
  {
    id: "admin",
    title: "Admin Module",
    icon: "admin",
    description:
      "Central control hub for the entire hospital — employees, patients, payments, departments, and operations in one dashboard.",
    features: [
      {
        title: "Dashboard",
        icon: "dashboard",
        description:
          "Holds complete list about employees, patients, payment details, medicine availability, operation report, birth and death report.",
      },
      {
        title: "Department",
        icon: "department",
        description:
          "Holds complete set of information regarding all departments like cardiology, gastroenterology with description explaining its core features.",
      },
      {
        title: "Notice Board",
        icon: "notice",
        description: "Update information regarding any new events or important programs scheduled.",
      },
    ],
  },
  {
    id: "doctors",
    title: "Doctors Module",
    icon: "doctors",
    description:
      "Admin holds the complete set of records about every medical expert and manages their day-to-day work.",
    features: [
      {
        title: "Qualification & Experience",
        icon: "qualification",
        description: "Holds the record of their education and work experience.",
      },
      {
        title: "Work Allocation",
        icon: "schedule",
        description: "Allocates shift timing for the entire week or month.",
      },
      {
        title: "Co-workers Details",
        icon: "coworkers",
        description: "Lists the details about nurses and other technicians working with them.",
      },
      {
        title: "Payment",
        icon: "payment",
        description: "Handles their payroll.",
      },
      {
        title: "Monitoring",
        icon: "monitoring-eye",
        description: "Monitors their interaction with patients.",
      },
      {
        title: "Appointment",
        icon: "appointment",
        description: "Helps in fixing appointment with patients.",
      },
    ],
  },
  {
    id: "patients",
    title: "Patients Module",
    icon: "patients",
    description:
      "Complete patient records with medical history, contact details, and case information managed in one place.",
    features: [
      {
        title: "Patient Profile",
        icon: "profile",
        items: [
          "Patient image",
          "Patient name",
          "Gender",
          "Date of Birth",
          "Age",
          "Blood group",
          "Case history",
          "Contact number",
          "E-mail id (if available)",
        ],
      },
    ],
  },
  {
    id: "nurses",
    title: "Nurses Module",
    icon: "nurses",
    description:
      "Complete information about nurses with professional activity monitoring for seamless day-to-day work.",
    features: [
      {
        title: "Qualification & Experience",
        icon: "qualification",
        description: "Holds the record of their education and work experience.",
      },
      {
        title: "Work Allocation",
        icon: "schedule",
        description: "Allocates shift timing for the entire week or month.",
      },
      {
        title: "Co-workers Details",
        icon: "coworkers",
        description: "Lists the details about doctors and lab technicians working with them.",
      },
      {
        title: "Payment",
        icon: "payment",
        description: "Handles their payroll.",
      },
      {
        title: "Personal Details",
        icon: "profile",
        description: "Holds complete information like name, age, address and contact number.",
      },
    ],
  },
  {
    id: "pharmacists",
    title: "Pharmacists Module",
    icon: "pharmacists",
    description:
      "Frequent interaction with pharmacists regarding drug availability and stock management.",
    features: [
      {
        title: "Qualification & Experience",
        icon: "qualification",
        description: "Holds the record of their education and work experience.",
      },
      {
        title: "Work Allocation",
        icon: "schedule",
        description: "Allocates shift timing for the entire week or month.",
      },
      {
        title: "Stock Maintenance",
        icon: "stock",
        description:
          "Frequent interaction with pharmacists regarding availability of drugs, their categories and other medicinal items.",
      },
      {
        title: "Payment",
        icon: "payment",
        description: "Handles their payroll.",
      },
      {
        title: "Personal Details",
        icon: "profile",
        description: "Holds complete information like name, age, address and contact number.",
      },
    ],
  },
  {
    id: "lab-technician",
    title: "Lab Technician Module",
    icon: "lab",
    description:
      "Admin manages lab technicians and their activities including equipment availability and patient test appointments.",
    features: [
      {
        title: "Qualification & Experience",
        icon: "qualification",
        description: "Holds the record of their education and work experience.",
      },
      {
        title: "Co-workers Details",
        icon: "coworkers",
        description: "Lists the details about doctors and lab technicians working with them.",
      },
      {
        title: "Stock Maintenance",
        icon: "stock",
        description:
          "Frequent interaction with lab technicians regarding availability of equipment and other test items.",
      },
      {
        title: "Payment",
        icon: "payment",
        description: "Handles their payroll.",
      },
      {
        title: "Fixing Appointments",
        icon: "appointment",
        description: "Intimates them about appointments with patients for lab tests.",
      },
      {
        title: "Personal Details",
        icon: "profile",
        description: "Holds complete information like name, age, address and contact number.",
      },
    ],
  },
  {
    id: "accountant",
    title: "Accountant Module",
    icon: "accountant",
    description: "Financial interaction hub for all hospital transactions and accounting activities.",
    features: [
      {
        title: "Financial Interaction",
        icon: "financial",
        description: "Interact with accountant regarding all financial transactions.",
      },
      {
        title: "Personal Details",
        icon: "profile",
        description: "Holds complete information like name, age, address and contact number.",
      },
    ],
  },
  {
    id: "receptionists",
    title: "Receptionists Module",
    icon: "receptionist",
    description:
      "Day-to-day front-desk activities and patient intake managed through the automated system.",
    features: [
      {
        title: "Day-to-Day Activities",
        icon: "activities",
        description: "Gather information from receptionist regarding day-to-day activities happening in the hospital.",
      },
      {
        title: "Personal Details",
        icon: "profile",
        description: "Holds complete information like name, age, address and contact number.",
      },
    ],
  },
  {
    id: "hospital-monitoring",
    title: "Hospital Monitoring",
    icon: "monitoring",
    description:
      "End-to-end monitoring of hospital accommodation, housekeeping, blood bank, and patient billing.",
    features: [
      {
        title: "Hospital Accommodation",
        icon: "accommodation",
        description: "Handling the complete stock of hospital accommodation and other amenities.",
      },
      {
        title: "House Keeping",
        icon: "housekeeping",
        description: "Managing cleaning and other housekeeping staffs.",
      },
      {
        title: "Blood Bank",
        icon: "blood-bank",
        description:
          "Keeps updated record of available blood groups, their quantity and the list of blood donors.",
      },
      {
        title: "Bill Payment",
        icon: "billing",
        description: "Handle patient payments without any obstacles.",
      },
    ],
  },
];

export const hmsAdvantages: HmsAdvantage[] = [
  {
    icon: "platform",
    text: "Fair automated platform that is tailor-made with functionalities for all user types",
  },
  {
    icon: "users",
    text: "Brings in 8 types of user accounts (Admin, Patients, Doctors, Nurses, Laboratorists, Pharmacists, Receptionists and Accountants)",
  },
  {
    icon: "appointment",
    text: "Fixing doctor's appointment has become easier",
  },
  {
    icon: "chat",
    text: "Enables patients to have one-to-one communication with doctors",
  },
  {
    icon: "bed",
    text: "Easy to track and manage hospital accommodations",
  },
  {
    icon: "drug",
    text: "Easy drug tracking",
  },
  {
    icon: "blood-bank",
    text: "Holds complete record on available group of blood",
  },
  {
    icon: "report",
    text: "Holds complete and accurate set of records of diagnostic reports",
  },
  {
    icon: "payroll",
    text: "Simple and effective payroll management system",
  },
  {
    icon: "package",
    text: "Complete package of automated software solution for hospitals that helps every department to do coordinated work in an effective way",
  },
];

export const hmsWorkflowSteps: HmsWorkflowStep[] = [
  {
    icon: "records",
    title: "Centralized Records",
    text: "Once the complete hospital management records are brought into the automated system, the admin takes care of all functionalities and brings in the key users and allocates them in their respective positions.",
  },
  {
    icon: "payroll",
    title: "Payroll Management",
    text: "Payroll management becomes easier with this automated process as it brings complete transparency on the work schedules and total number of working days for doctors and other staff.",
  },
  {
    icon: "registration",
    title: "Staff Registration",
    text: "Doctors and other working staff need to get registered by providing valid and complete information from their side. This will also be verified by the admin and added in the respective sections.",
  },
  {
    icon: "stock",
    title: "Stock Management",
    text: "The complete stock details are added to this automation process by every department — pharmacy adding drug details and lab technicians adding details regarding instruments.",
  },
  {
    icon: "supply",
    title: "Demand & Supply Balance",
    text: "Adding these stock details helps the admin maintain a proper balance between demand and supply.",
  },
  {
    icon: "hr",
    title: "Human Resource Allocation",
    text: "Using the automated system, admin can now allocate human resources properly according to their availability and slot them in different shifts.",
  },
  {
    icon: "patients",
    title: "Patient Registration",
    text: "Patients need to register by providing their complete information. Once the information is verified from the admin side they are added to the automated system.",
  },
  {
    icon: "ai",
    title: "AI-Assisted Workflow",
    text: "We have deployed a structured working process in this automated hospital management software where the role of every employee is assisted in an efficient way by the AI tool.",
  },
];

export const hmsUserRoles: HmsUserRole[] = [
  {
    title: "For Doctors",
    icon: "doctors",
    intro:
      "Admin holds the complete set of records about every medical expert working in the hospital and takes care of the following functionalities:",
    items: [
      "Holds the record of their education and work experience",
      "Allocates shift timing for the entire week or month",
      "Lists the details about nurses and other technicians working with them",
      "Handles their payroll",
      "Monitors their interaction with patients",
      "Helps in fixing appointment with patients",
    ],
  },
  {
    title: "For Nurses",
    icon: "nurses",
    intro:
      "Similar to doctors, admin also holds complete information about the nurses working in the hospital and monitors their professional activities through this automated system.",
    items: [
      "Holds the record of their education and work experience",
      "Allocates shift timing for the entire week or month",
      "Lists the details about doctors and lab technicians working with them",
      "Our custom-made automated hospital management software incorporates all functionalities of every staff member according to their role",
    ],
  },
  {
    title: "For Lab Technicians",
    icon: "lab",
    intro:
      "Admin takes care of lab technicians and their activities in the automated hospital system with the following support:",
    items: [
      "Holds the record of their education and work experience",
      "Lists the details about doctors and lab technicians working with them",
      "Frequent interaction with lab technicians regarding availability of equipment and other test items",
      "Handles their payroll",
      "Intimates them about appointments with patients for lab tests",
      "Helps in fixing appointment with patients",
    ],
  },
  {
    title: "For Pharmacists",
    icon: "pharmacists",
    intro:
      "Admin keeps frequently interacting with pharmacists regarding availability of drugs and other medicinal items:",
    items: [
      "Holds the record of their education and work experience",
      "Allocates shift timing for the entire week or month",
      "Frequent interaction with pharmacists regarding availability of drugs, their categories and other medicinal items",
      "Handles their payroll",
    ],
  },
  {
    title: "Other Admin Responsibilities",
    icon: "settings",
    intro:
      "Apart from staff management, admin also handles critical hospital operations:",
    items: [
      "Handling the complete stock of hospital accommodation and other amenities",
      "Managing cleaning and other housekeeping staff",
      "Managing blood donors",
      "Handle patient payments without any obstacles",
      "Address and fix traditional issues faced by pharmacies and laboratories of the hospital",
    ],
  },
];
